<template>
    <auth-layout
        :isPlain="!isJustRegistered"
        :image="1"
        :progress="0"
        :isProgressBarHidden="true"
        :byword="$t('pages.auth.signup.steps.physician_password.byword')"
    >
        <common-content-wrapper class="practice-membership__text-left" width="small">
            <div v-if="showIcon" class="practice-membership__icon">
                <common-confirmation-icon :type="iconType"></common-confirmation-icon>
            </div>

            <common-page-header :heading="heading" :content="content" contentSpacing="large"></common-page-header>

            <p
                v-if="practiceMembershipApproved"
                class="page-header page-header__content practice-membership__confirmation-link"
                v-html="
                    $t('pages.auth.signup.steps.practice_membership.please_use_confirmation_link', {
                        practiceName: practice.name,
                        email: user.email,
                    })
                "
            ></p>

            <div class="page-copy">
                <hr />
            </div>

            <p v-if="!isConfirmationEmailSent" v-html="footer"></p>

            <div v-if="practiceMembershipApproved">
                <p v-if="!isConfirmationEmailSent">
                    <button type="button" @click="resendVerificationEmail()">
                        <a>
                            {{ $t('pages.auth.signup.steps.practice_membership.buttons.resend') }}
                        </a>
                    </button>
                    {{ $t('pages.auth.signup.steps.practice_membership.resend_email') }}
                </p>
                <p v-else>
                    {{ $t('pages.auth.signup.steps.practice_membership.email_sent') }}
                </p>
            </div>
        </common-content-wrapper>
    </auth-layout>
</template>

<script>
    import SignupPageMixin from 'mixins/SignupPageMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import AuthLayout from 'components/layouts/AuthLayout'
    import UserMembershipStatusEnum from '../../../enums/UserMembershipStatusEnum'
    import SkipStepService from '../../../services/SkipStepService'
    import UsersAPI from 'api/UsersAPI'
    import AuthAPI from 'api/AuthAPI'
    import { isRouteNameEqual } from '@/helpers'
    import GoogleAnalyticsService from 'services/GoogleAnalyticsService'

    export default {
        mixins: [SignupPageMixin, PageWithDataMixin],

        components: {
            AuthLayout,
        },

        data() {
            return {
                user: this.$auth.getUser(),
                practice: this.$auth.getPractice(),
                isJustRegistered: false,
                isResendEmailProcessing: false,
                isConfirmationEmailSent: false,
            }
        },

        computed: {
            heading() {
                if (this.practiceMembershipDeclined) {
                    return this.$t('pages.auth.signup.steps.practice_membership.membership_denied_heading')
                } else if (this.practiceMembershipRevoked) {
                    return this.$t('pages.auth.signup.steps.practice_membership.membership_revoked_heading')
                } else if (this.practiceMembershipApproved) {
                    return this.$t('pages.auth.signup.steps.practice_membership.membership_approved_heading')
                } else if (this.isJustRegistered) {
                    return this.$t('pages.auth.signup.steps.practice_membership.membership_just_confirmed_heading')
                }
                return this.$t('pages.auth.signup.steps.practice_membership.membership_pending_heading')
            },

            content() {
                if (this.practice && this.practiceMembershipDeclined) {
                    return this.$t('pages.auth.signup.steps.practice_membership.membership_denied_content', {
                        practiceName: this.practice.name,
                    })
                } else if (this.practice && this.practiceMembershipRevoked) {
                    return this.$t('pages.auth.signup.steps.practice_membership.membership_revoked_content', {
                        practiceName: this.practice.name,
                    })
                } else if (this.practiceMembershipApproved) {
                    return this.$t('pages.auth.signup.steps.practice_membership.membership_approved_content')
                }
                return this.$t('pages.auth.signup.steps.practice_membership.membership_pending_content')
            },

            footer() {
                if (this.practiceMembershipDeclined || this.practiceMembershipRevoked) {
                    return this.$t('pages.auth.signup.steps.practice_membership.membership_denied_footer')
                } else if (this.practiceMembershipApproved) {
                    return this.$t('pages.auth.signup.steps.practice_membership.membership_approved_footer')
                }
                return this.$t('pages.auth.signup.steps.practice_membership.membership_pending_footer', {
                    email: this.$auth.getUser().email,
                })
            },

            practiceMembershipDeclined() {
                return this.user.practiceMembershipStatus === UserMembershipStatusEnum.DECLINED
            },

            practiceMembershipApproved() {
                return this.user.practiceMembershipStatus === UserMembershipStatusEnum.APPROVED
            },

            practiceMembershipRevoked() {
                return this.user.practiceMembershipStatus === UserMembershipStatusEnum.MEMBERSHIP_REVOKED
            },

            iconType() {
                return this.practiceMembershipDeclined || this.practiceMembershipRevoked ? 'error' : 'success'
            },

            showIcon() {
                return this.isJustRegistered || this.practiceMembershipDeclined || this.practiceMembershipRevoked
            },
        },

        methods: {
            resendVerificationEmail() {
                if (!this.isResendEmailProcessing) {
                    this.isResendEmailProcessing = true
                    AuthAPI.sendVerificationEmail(this.user.id).then(() => {
                        this.isResendEmailProcessing = false
                        this.isConfirmationEmailSent = true
                    })
                }
            },
        },

        mounted() {
            this.isJustRegistered = SkipStepService.shouldShowStep('isUserJustRegistered')
            if (this.$router.previousRoute && isRouteNameEqual(this.$router.previousRoute.name, 'auth.signup', 2)) {
                if (process.env.VUE_APP_ENV === 'production') {
                    UsersAPI.sendNewMemberRegisteredNotificationEmail()
                }
                GoogleAnalyticsService.emitRegistrationCompletedGoogleAnalytics(this.$ga)
            }
        },
    }
</script>

<style lang="scss" scoped>
    .practice-membership {
        &__text-left {
            text-align: left;
        }

        &__icon {
            display: flex;
            justify-content: center;
            padding-bottom: 5%;
        }

        &__confirmation-link {
            margin-top: -4%;
        }
    }
</style>
